import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import VerifiedBadge from "@/components/VerifiedBadge";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MagnifyingGlass, IdentificationBadge, MapPin, Briefcase, CalendarBlank, Heart, PaperPlaneRight, X } from "@phosphor-icons/react";

/* v2.90 · Public PE directory. Clients (and admin) filter by country,
   state/province, min years of practice, and specialization. Shows the
   `profile_live` snapshot only — pending edits stay invisible until
   admin approval. */

export default function DirectoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [facets, setFacets] = useState({ countries: [], regions: [], specializations: [], total: 0 });
  const [filters, setFilters] = useState({ country: "", region: "", min_years: "", specialization: "", sort: "years_desc", bookable: false, favorites_only: false, q: "" });
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState([]);
  const [savedName, setSavedName] = useState("");
  const [favMap, setFavMap] = useState(new Map());   // pe_id → {note, order}
  const [favBusy, setFavBusy] = useState("");
  const [dragId, setDragId] = useState("");
  const [notesEditId, setNotesEditId] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  // v3.2.28 · Multi-select for bulk job invitation.
  const [selected, setSelected] = useState(new Set());
  const [inviteModal, setInviteModal] = useState(false);
  const [openJobs, setOpenJobs] = useState([]);
  const [chosenJob, setChosenJob] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const favIds = favMap;   // legacy alias — .has(id) still works
  const assetUrl = (ref) => ref ? `${getBackendUrl()}/api/pe/assets/${ref}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.country) params.country = filters.country;
      if (filters.region) params.region = filters.region;
      if (filters.min_years !== "") params.min_years = filters.min_years;
      if (filters.specialization) params.specialization = filters.specialization;
      if (filters.sort) params.sort = filters.sort;
      if (filters.bookable) params.bookable = true;
      if (filters.favorites_only) params.favorites_only = true;
      if (filters.q && filters.q.trim()) params.q = filters.q.trim();
      const { data } = await api.get("/directory/pes", { params });
      setItems(data);
      // v3.2.61 · Only ask for suggestions when the filter mix
      // actually maps to backend `/directory/pes/suggestions` inputs.
      // `bookable` and `favorites_only` aren't accepted by that
      // endpoint, so an empty-filtered result on them shouldn't
      // trigger an irrelevant "top favorited" fallback.
      const supportedFilters = !!(params.country || params.region ||
        params.specialization || (params.min_years !== undefined));
      if (data.length === 0 && supportedFilters) {
        try {
          const { data: sugs } = await api.get("/directory/pes/suggestions",
            { params: {
                country: params.country || undefined,
                region: params.region || undefined,
                specialization: params.specialization || undefined,
                min_years: params.min_years,
              } });
          setSuggestions(sugs || []);
        } catch { setSuggestions([]); }
      } else {
        setSuggestions([]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load directory");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/directory/facets").then(({ data }) => setFacets(data)).catch(() => {});
    api.get("/directory/saved-searches").then(({ data }) => setSaved(data)).catch(() => {});
    // v3.2.26/27 · Load favorites (dicts with pe_id + note + order);
    // mark the directory as opened so saved-search "new match" badges
    // reset on next refresh.
    api.get("/directory/favorites").then(({ data }) => {
      const items = data.items || (data.ids || []).map((id, i) => ({ pe_id: id, note: "", order: i }));
      const m = new Map();
      items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      items.forEach((it) => m.set(it.pe_id, { note: it.note || "", order: it.order ?? 0 }));
      setFavMap(m);
    }).catch(() => {});
    api.post("/directory/mark-opened").catch(() => {});
  }, []);
  // v3.2.23 · Debounce keystroke-driven reloads by 250 ms so the search
  // box doesn't hammer the API on every character at scale.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  const clear = () => setFilters({ country: "", region: "", min_years: "", specialization: "", sort: "years_desc", bookable: false, favorites_only: false, q: "" });

  const toggleFavorite = async (peId) => {
    setFavBusy(peId);
    const wasFav = favMap.has(peId);
    // Optimistic
    setFavMap((prev) => {
      const next = new Map(prev);
      if (wasFav) next.delete(peId);
      else next.set(peId, { note: "", order: next.size });
      return next;
    });
    try {
      if (wasFav) await api.delete(`/directory/favorites/${peId}`);
      else        await api.post(`/directory/favorites/${peId}`);
    } catch (e) {
      // Rollback
      setFavMap((prev) => {
        const next = new Map(prev);
        if (wasFav) next.set(peId, { note: "", order: next.size });
        else next.delete(peId);
        return next;
      });
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Favorite failed");
    } finally { setFavBusy(""); }
  };

  const saveNote = async (peId) => {
    try {
      await api.patch(`/directory/favorites/${peId}`, { note: noteDraft });
      setFavMap((prev) => {
        const next = new Map(prev);
        const cur = next.get(peId) || {};
        next.set(peId, { ...cur, note: noteDraft });
        return next;
      });
      toast.success("Note saved");
      setNotesEditId("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(""); return; }
    // Reorder items[] client-side, then persist.
    const ids = items.filter((i) => favMap.has(i.id)).map((i) => i.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(""); return; }
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    // Optimistic — reorder items[] locally in favorites_only view.
    setItems((prev) => {
      const rank = new Map(ids.map((id, i) => [id, i]));
      const copy = [...prev];
      copy.sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
      return copy;
    });
    setDragId("");
    try {
      await api.post("/directory/favorites-reorder", { ordered_pe_ids: ids });
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  const saveCurrent = async () => {
    if (!savedName.trim()) { toast.error("Give this search a name first."); return; }
    try {
      await api.post("/directory/saved-searches", { name: savedName, filters });
      const { data } = await api.get("/directory/saved-searches");
      setSaved(data); setSavedName("");
      toast.success("Search saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const runSaved = (s) => setFilters({ ...filters, ...s.filters });

  const deleteSaved = async (sid) => {
    try {
      await api.delete(`/directory/saved-searches/${sid}`);
      setSaved(saved.filter((s) => s.id !== sid));
    } catch (e) { toast.error("Delete failed"); }
  };

  // v3.2.28 · Multi-select for bulk invite.
  const toggleSelect = (peId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(peId)) next.delete(peId);
      else next.add(peId);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAllFavorites = () => {
    const ids = items.filter((i) => favMap.has(i.id)).map((i) => i.id);
    setSelected(new Set(ids));
  };

  const openInviteModal = async () => {
    if (selected.size === 0) return;
    setInviteBusy(true);
    try {
      // Client sees jobs in `open` + `received` buckets — both are still open.
      const [openRes, receivedRes] = await Promise.all([
        api.get("/jobs", { params: { bucket: "open" } }),
        api.get("/jobs", { params: { bucket: "received" } }),
      ]);
      const merged = [...(openRes.data || []), ...(receivedRes.data || [])]
        .filter((j) => j.status === "open");
      setOpenJobs(merged);
      setChosenJob(merged[0]?.id || "");
      setInviteModal(true);
    } catch (e) {
      toast.error("Couldn't load your jobs");
    } finally { setInviteBusy(false); }
  };

  const sendBulkInvite = async () => {
    if (!chosenJob || selected.size === 0) return;
    setInviteBusy(true);
    try {
      const { data } = await api.post(`/jobs/${chosenJob}/invite-bulk`,
        { pe_ids: Array.from(selected) });
      const invited = data.counts?.invited ?? 0;
      const skipped = data.counts?.skipped ?? 0;
      if (invited > 0) {
        toast.success(`Invited ${invited} PE${invited !== 1 ? "s" : ""}${
          skipped > 0 ? ` · ${skipped} skipped (already invited or bidding)` : ""
        }`);
      } else {
        toast.info(`No new invites sent — all ${skipped} were already invited or bidding.`);
      }
      setInviteModal(false);
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed");
    } finally { setInviteBusy(false); }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// directory</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">Licensed Professional Engineers</h1>
        <p className="text-gray-400 mt-2 max-w-2xl">Every profile here has been vetted by our admin team. Filter by country, region, years of practice, and specialization.</p>

        {/* v3.2.23 · Search bar + bookable filter chip. */}
        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="dir-search-bar">
          <div className="flex-1 min-w-[240px] flex items-center gap-2 border border-white/10 bg-black/40 px-3 focus-within:border-[#00E5FF]">
            <MagnifyingGlass size={16} className="text-gray-500 flex-none" />
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search bio, specializations, or name…"
              className="flex-1 bg-transparent py-2 text-sm font-mono outline-none"
              data-testid="dir-search-input"
            />
            {filters.q && (
              <button onClick={() => setFilters({ ...filters, q: "" })}
                      className="text-gray-500 hover:text-white text-lg leading-none px-1"
                      title="Clear search"
                      data-testid="dir-search-clear">×</button>
            )}
          </div>
          <button
            onClick={() => setFilters({ ...filters, bookable: !filters.bookable })}
            className={`inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 border transition-colors ${
              filters.bookable
                ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10"
                : "border-white/15 text-gray-400 hover:border-white/30 hover:text-white"
            }`}
            data-testid="dir-filter-bookable"
            aria-pressed={filters.bookable}
          >
            <CalendarBlank size={12} weight={filters.bookable ? "fill" : "regular"} />
            bookable only
          </button>
          {/* v3.2.26 · Favorites-only filter chip. */}
          <button
            onClick={() => setFilters({ ...filters, favorites_only: !filters.favorites_only })}
            className={`inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 border transition-colors ${
              filters.favorites_only
                ? "border-[#FF8A85] text-[#FF8A85] bg-[#FF8A85]/10"
                : "border-white/15 text-gray-400 hover:border-white/30 hover:text-white"
            }`}
            data-testid="dir-filter-favorites"
            aria-pressed={filters.favorites_only}
          >
            <Heart size={12} weight={filters.favorites_only ? "fill" : "regular"} />
            favorites{favIds.size > 0 ? ` · ${favIds.size}` : ""}
          </button>
          {/* v3.2.25 · Live "Showing X of Y" pill — reflects the current
              filtered result count against the universe from /facets. */}
          <div
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-3 py-2 border border-white/10 bg-white/[0.02] text-gray-300 tabular-nums"
            data-testid="dir-result-count"
            aria-live="polite"
          >
            {loading ? (
              <span className="text-gray-500">counting…</span>
            ) : (
              <>
                showing <span className="text-[#00E5FF]" data-testid="dir-result-count-x">{items.length}</span>
                <span className="text-gray-500 mx-0.5">of</span>
                <span className="text-white" data-testid="dir-result-count-y">{facets.total || items.length}</span>
              </>
            )}
          </div>
        </div>

        {/* ─── Filters ────────────────────────────────────────────── */}
        <div className="mt-8 border border-white/10 bg-[#12151C] p-4 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="dir-filters">
          <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value, region: "" })}
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-filter-country">
            <option value="">Any country</option>
            {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-filter-region">
            <option value="">Any region</option>
            {facets.regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input type="number" min={0} value={filters.min_years}
            onChange={(e) => setFilters({ ...filters, min_years: e.target.value })}
            placeholder="Min years"
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-filter-years" />
          <select value={filters.specialization} onChange={(e) => setFilters({ ...filters, specialization: e.target.value })}
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-filter-spec">
            <option value="">Any specialization</option>
            {facets.specializations.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
            className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-filter-sort">
            <option value="years_desc">Sort · most years first</option>
            <option value="years_asc">Sort · least years first</option>
            <option value="name_asc">Sort · name A-Z</option>
            <option value="region_asc">Sort · region A-Z</option>
          </select>
          <button onClick={clear}
            className="border border-white/20 text-sm font-mono hover:bg-white/5"
            data-testid="dir-filter-clear">
            Clear filters
          </button>
        </div>

        {/* ─── Saved searches ────────────────────────────────────── */}
        <div className="mt-4 border border-white/10 bg-white/5 p-3 flex flex-wrap items-center gap-2" data-testid="dir-saved">
          <span className="label-tag !text-gray-400">// saved</span>
          {saved.length === 0 && <span className="text-xs text-gray-500 font-mono">No saved searches yet.</span>}
          {saved.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] px-2 py-0.5 text-xs font-mono"
                  data-testid={`dir-saved-${s.id}`}>
              <button onClick={() => runSaved(s)} className="hover:underline">{s.name}</button>
              {/* v3.2.26 · New-match badge. Hidden when 0. */}
              {(s.new_match_count || 0) > 0 && (
                <span className="bg-[#FFCC00]/20 border border-[#FFCC00]/50 text-[#FFCC00] px-1 py-0 text-[9px] tabular-nums"
                      title={`${s.new_match_count} new matches since your last visit`}
                      data-testid={`dir-saved-new-${s.id}`}>
                  +{s.new_match_count} NEW
                </span>
              )}
              <button onClick={() => deleteSaved(s.id)} className="hover:text-white ml-1" title="Remove">×</button>
            </span>
          ))}
          <input value={savedName} onChange={(e) => setSavedName(e.target.value)}
            placeholder="Save current filters as…"
            className="flex-1 min-w-[160px] bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
            data-testid="dir-saved-name" />
          <button onClick={saveCurrent}
            className="text-xs text-[#00E5FF] hover:underline font-mono" data-testid="dir-saved-add">
            + save
          </button>
        </div>

        {/* v3.2.28 · Bulk-action bar — only appears in favorites_only mode for clients. */}
        {isClient && filters.favorites_only && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border border-[#00E5FF]/20 bg-[#00E5FF]/[0.03] px-3 py-2"
               data-testid="dir-bulk-bar">
            <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-wider">
              // bulk invite
            </span>
            <span className="text-xs text-gray-400" data-testid="dir-bulk-selected-count">
              <span className="text-white font-bold tabular-nums">{selected.size}</span> selected
            </span>
            <button onClick={selectAllFavorites}
              className="text-xs font-mono text-gray-400 hover:text-white underline-offset-4 hover:underline"
              data-testid="dir-bulk-select-all">
              select all favorites
            </button>
            {selected.size > 0 && (
              <button onClick={clearSelection}
                className="text-xs font-mono text-gray-400 hover:text-white underline-offset-4 hover:underline"
                data-testid="dir-bulk-clear">
                clear
              </button>
            )}
            <div className="flex-1" />
            <button onClick={openInviteModal}
              disabled={selected.size === 0 || inviteBusy}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-3 py-2 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              data-testid="dir-bulk-invite-btn">
              <PaperPlaneRight size={12} weight="fill" />
              Invite {selected.size > 0 ? `${selected.size} ` : ""}to a job
            </button>
          </div>
        )}

        {/* ─── Results ────────────────────────────────────────────── */}
        <div className="mt-6 text-xs font-mono text-gray-500" data-testid="dir-results-status">
          {loading ? "Searching…" : items.length === 0 ? "No engineers match — try clearing filters." : null}
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((pe) => (
            <div key={pe.id} role="link" tabIndex={0}
              onClick={() => navigate(`/directory/pes/${pe.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/directory/pes/${pe.id}`); }}
              draggable={filters.favorites_only && favMap.has(pe.id)}
              onDragStart={() => onDragStart(pe.id)}
              onDragOver={filters.favorites_only ? onDragOver : undefined}
              onDrop={filters.favorites_only ? () => onDrop(pe.id) : undefined}
              className={`relative border border-white/10 bg-[#12151C] p-5 hover:border-[#00E5FF]/60 transition group cursor-pointer ${
                filters.favorites_only && favMap.has(pe.id) ? "cursor-move" : ""
              } ${dragId === pe.id ? "opacity-40" : ""}`}
              data-testid={`dir-card-${pe.id}`}>
              {/* v3.2.24 · Recent-projects hover tooltip. Visible on card
                  hover + keyboard focus. Positioned absolute above the
                  card so it doesn't reflow the grid. */}
              {pe.recent_projects && pe.recent_projects.length > 0 && (
                <div
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 z-20 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150"
                  data-testid={`dir-card-projects-tip-${pe.id}`}
                >
                  <div className="border border-[#00E5FF]/40 bg-[#0A0C10] shadow-[0_8px_24px_rgba(0,0,0,0.6)] p-3">
                    <div className="text-[9px] font-mono text-[#FFCC00] uppercase tracking-[0.2em] mb-2">
                      // recent projects
                    </div>
                    <ul className="space-y-1">
                      {pe.recent_projects.map((rp, i) => (
                        <li key={i} className="text-xs text-white truncate"
                            data-testid={`dir-card-projects-tip-${pe.id}-item-${i}`}
                            title={rp.title}>
                          <span className="text-[#00E5FF] mr-1">▸</span>{rp.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Tooltip anchor pip */}
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-[#0A0C10] border-r border-b border-[#00E5FF]/40" />
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 border border-white/10 bg-black/40 overflow-hidden flex-none">
                  {pe.picture_ref
                    ? <img src={assetUrl(pe.picture_ref)} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-600"><IdentificationBadge size={30} weight="duotone" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold truncate group-hover:text-[#00E5FF] transition">
                    {pe.name}
                    <VerifiedBadge verified={pe.email_verified} size={15} />
                  </div>
                  <div className="mt-1 text-xs font-mono text-gray-400 inline-flex items-center gap-1">
                    <MapPin size={11} /> {pe.region ? `${pe.region}, ${pe.country}` : pe.country || "—"}
                  </div>
                  <div className="mt-0.5 text-xs font-mono text-gray-400 inline-flex items-center gap-1">
                    <Briefcase size={11} /> {pe.years_of_practice} years
                  </div>
                  {/* v3.2.21 · Bookable indicator — clickable "Book" chip that opens the PE's Teams/Calendly URL in a new tab. */}
                  {pe.booking_url && /^https?:\/\//i.test(pe.booking_url) && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(pe.booking_url, "_blank", "noopener,noreferrer"); }}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-2 py-0.5"
                      title="Book a meeting"
                      data-testid={`dir-card-book-${pe.id}`}
                    >
                      <CalendarBlank size={10} weight="fill" /> book a meeting
                    </button>
                  )}
                </div>
                {/* v3.2.26 · Favorite heart — top-right of the card. */}
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(pe.id); }}
                  disabled={favBusy === pe.id}
                  aria-pressed={favIds.has(pe.id)}
                  aria-label={favIds.has(pe.id) ? "Unfavorite" : "Favorite"}
                  className={`absolute top-3 right-3 p-1.5 transition-colors ${
                    favIds.has(pe.id)
                      ? "text-[#FF8A85] hover:text-[#FF8A85]/70"
                      : "text-gray-600 hover:text-[#FF8A85]"
                  } disabled:opacity-40`}
                  data-testid={`dir-card-fav-${pe.id}`}
                >
                  <Heart size={18} weight={favIds.has(pe.id) ? "fill" : "regular"} />
                </button>
                {/* v3.2.28 · Multi-select checkbox — only visible in
                    favorites_only mode for clients (source of bulk
                    invite). Positioned top-left to avoid the heart. */}
                {isClient && filters.favorites_only && favMap.has(pe.id) && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(pe.id); }}
                    className={`absolute top-3 left-3 w-5 h-5 border-2 flex items-center justify-center transition-colors ${
                      selected.has(pe.id)
                        ? "bg-[#00E5FF] border-[#00E5FF]"
                        : "bg-black/50 border-white/30 hover:border-[#00E5FF]"
                    }`}
                    aria-checked={selected.has(pe.id)}
                    role="checkbox"
                    aria-label={selected.has(pe.id) ? "Deselect" : "Select"}
                    data-testid={`dir-card-select-${pe.id}`}
                  >
                    {selected.has(pe.id) && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-6" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              {pe.bio && <p className="mt-3 text-xs text-gray-300 line-clamp-3">{pe.bio}</p>}
              <div className="mt-3 flex flex-wrap gap-1">
                {(pe.specializations || []).slice(0, 3).map((s) => (
                  <span key={s} className="text-[10px] font-mono border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] px-1.5 py-0.5">{s}</span>
                ))}
                {(pe.specializations || []).length > 3 && (
                  <span className="text-[10px] font-mono text-gray-500">+{(pe.specializations || []).length - 3} more</span>
                )}
              </div>
              {/* v3.2.27 · Private note per favorited PE — visible only
                  when the PE is favorited. Inline edit; auto-collapsed. */}
              {favMap.has(pe.id) && (
                <div className="mt-3 border-t border-white/5 pt-3" onClick={(e) => e.stopPropagation()}>
                  {notesEditId === pe.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value.slice(0, 500))}
                        onKeyDown={(e) => e.stopPropagation()}
                        rows={2}
                        placeholder="private note…"
                        className="w-full bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono text-white focus:border-[#00E5FF] outline-none resize-none"
                        data-testid={`dir-card-note-textarea-${pe.id}`}
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <button onClick={() => saveNote(pe.id)}
                          className="text-[10px] font-mono px-2 py-1 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                          data-testid={`dir-card-note-save-${pe.id}`}>save</button>
                        <button onClick={() => setNotesEditId("")}
                          className="text-[10px] font-mono text-gray-500 hover:text-white">cancel</button>
                        <span className="text-[10px] font-mono text-gray-500 ml-auto">{noteDraft.length}/500</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setNotesEditId(pe.id); setNoteDraft(favMap.get(pe.id)?.note || ""); }}
                      className="w-full text-left text-[11px] font-mono text-gray-400 hover:text-[#00E5FF] transition-colors truncate"
                      title={favMap.get(pe.id)?.note || "click to add a private note"}
                      data-testid={`dir-card-note-open-${pe.id}`}>
                      {favMap.get(pe.id)?.note
                        ? <span>▸ <span className="text-white">{favMap.get(pe.id).note}</span></span>
                        : <span>+ add private note</span>}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="col-span-full" data-testid="dir-empty">
              <div className="border border-white/10 bg-white/5 p-8 text-center text-sm font-mono text-gray-400">
                <MagnifyingGlass size={22} className="mx-auto mb-2 text-gray-500" />
                No engineers match those filters yet.
                {suggestions.length > 0 && (
                  <div className="mt-1 text-[11px] text-[#00E5FF] uppercase tracking-widest">
                    // closest matches below
                  </div>
                )}
              </div>
              {suggestions.length > 0 && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                      data-testid="dir-suggestions">
                  {suggestions.map((pe) => (
                    <div key={pe.id}
                          onClick={() => navigate(`/directory/pes/${pe.id}`)}
                          className="cursor-pointer border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] hover:bg-[#00E5FF]/[0.08] p-4 transition"
                          data-testid={`dir-suggestion-${pe.id}`}>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#00E5FF] mb-1">
                        matches: {pe.matched_filters?.length ? pe.matched_filters.join(" · ") : "top-rated"}
                      </div>
                      <div className="font-display text-base font-black text-white truncate">{pe.name}</div>
                      <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                        {[pe.region, pe.country].filter(Boolean).join(", ") || "—"}
                        {pe.years_of_practice ? `  ·  ${pe.years_of_practice} yr` : ""}
                      </div>
                      {pe.bio && <div className="mt-2 text-xs text-gray-400 line-clamp-2">{pe.bio}</div>}
                      {pe.specializations?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {pe.specializations.slice(0, 4).map((s) => (
                            <span key={s} className="text-[9px] font-mono border border-white/15 px-1.5 py-0.5 text-gray-300">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* v3.2.28 · Bulk-invite modal. */}
      {inviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
             onClick={() => setInviteModal(false)}
             data-testid="dir-invite-modal">
          <div className="w-full max-w-lg bg-[#0A0C10] border border-[#00E5FF]/40 p-6 relative"
               onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setInviteModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-white"
              data-testid="dir-invite-modal-close">
              <X size={18} />
            </button>
            <div className="label-tag">// invite {selected.size} PE{selected.size !== 1 ? "s" : ""}</div>
            <h2 className="font-display text-2xl font-black mt-1">Pick a job</h2>
            <p className="text-xs text-gray-400 mt-2">
              Selected engineers will get an in-app notification and an email inviting them to
              submit a proposal. Only open jobs are shown.
            </p>

            {openJobs.length === 0 ? (
              <div className="mt-6 border border-white/10 bg-white/5 p-4 text-sm text-gray-400 font-mono"
                   data-testid="dir-invite-no-jobs">
                No open jobs on your account. Post one first, then come back to invite.
                <button onClick={() => { setInviteModal(false); navigate("/jobs/new"); }}
                  className="mt-3 block text-[#00E5FF] hover:underline text-xs font-mono"
                  data-testid="dir-invite-post-job">
                  → Post a job
                </button>
              </div>
            ) : (
              <>
                <div className="mt-5 space-y-2 max-h-64 overflow-auto border border-white/10 p-2"
                     data-testid="dir-invite-jobs-list">
                  {openJobs.map((j) => (
                    <label key={j.id}
                      className={`flex items-start gap-3 p-3 cursor-pointer transition ${
                        chosenJob === j.id
                          ? "bg-[#00E5FF]/10 border border-[#00E5FF]/40"
                          : "border border-transparent hover:bg-white/5"
                      }`}
                      data-testid={`dir-invite-job-${j.id}`}>
                      <input type="radio" name="invite-job" value={j.id}
                        checked={chosenJob === j.id}
                        onChange={() => setChosenJob(j.id)}
                        className="mt-1 accent-[#00E5FF]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{j.title}</div>
                        <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                          ${(j.budget_estimate_usd || 0).toLocaleString()} USD
                          {j.deadline ? ` · due ${j.deadline}` : ""}
                          {j.proposals_count > 0 ? ` · ${j.proposals_count} proposal${j.proposals_count !== 1 ? "s" : ""}` : ""}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button onClick={() => setInviteModal(false)}
                    className="text-xs font-mono text-gray-400 hover:text-white px-3 py-2"
                    data-testid="dir-invite-cancel">
                    cancel
                  </button>
                  <button onClick={sendBulkInvite}
                    disabled={!chosenJob || inviteBusy}
                    className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-4 py-2 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed"
                    data-testid="dir-invite-send">
                    <PaperPlaneRight size={12} weight="fill" />
                    {inviteBusy ? "Sending…" : `Send ${selected.size} invite${selected.size !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
